import type { DefaultOptionType } from '@/components/combobox/combobox';
import { PageWrapper } from '@/components/page-wrapper/page-wrapper';
import { useErrorNotification } from '@/hooks/use-error-notification';
import { useNotification } from '@/hooks/use-notification';
import { normalizeApiError } from '@/utils/error-utils';
import { useRef, useState } from 'react';
import ResetNoveltyPricesConfirmModal from './reset-novelty-prices-confirm-modal';
import { useResetNoveltyPrices } from './reset-novelty-prices-queries';
import ResetNoveltyPricesSetupPanel from './reset-novelty-prices-setup-panel';

export const ResetNoveltyPricesPage = () => {
  const { mutate: resetNoveltyPrices, isPending } = useResetNoveltyPrices();
  const showNotification = useNotification();
  const showErrorNotification = useErrorNotification();
  const confirmTriggerRef = useRef<HTMLButtonElement | null>(null);

  const [selectedPriceUnits, setSelectedPriceUnits] = useState<
    DefaultOptionType[]
  >([]);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const selectedPriceUnit = selectedPriceUnits[0];

  const handleConfirm = () => {
    if (!selectedPriceUnit) {
      return;
    }

    resetNoveltyPrices(
      {
        deleteNoveltyPricesRequest: {
          priceUnitId: Number(selectedPriceUnit.id),
        },
      },
      {
        onSuccess: (response) => {
          if (response.errors && response.errors.length > 0) {
            showNotification({
              type: 'error',
              title: 'Unable to reset novelty prices',
              description: response.errors.join('; '),
            });
            return;
          }

          const deletedCount = response.deletedCount;
          showNotification({
            type: 'success',
            title: 'Novelty prices reset',
            description:
              deletedCount === undefined
                ? 'Novelty prices were deleted successfully.'
                : `Deleted ${deletedCount} novelty prices.`,
          });

          setShowConfirmModal(false);
          setSelectedPriceUnits([]);
        },
        onError: (error) => {
          showErrorNotification(
            normalizeApiError(error),
            'reset novelty prices',
          );
          setShowConfirmModal(false);
        },
      },
    );
  };

  return (
    <PageWrapper
      headerProps={{
        title: 'Reset Novelty Prices',
      }}
      layout="fill"
      contentPaddingX="0px"
      maxWidth="100%"
    >
      <div style={{ display: 'flex', height: '100%' }}>
        <ResetNoveltyPricesSetupPanel
          selectedPriceUnits={selectedPriceUnits}
          setSelectedPriceUnits={setSelectedPriceUnits}
          onSubmit={() => setShowConfirmModal(true)}
          isLoading={isPending}
          triggerRef={confirmTriggerRef}
        />
      </div>
      <ResetNoveltyPricesConfirmModal
        isOpen={showConfirmModal}
        triggerRef={confirmTriggerRef}
        priceUnitName={selectedPriceUnit?.label ?? ''}
        isDeleting={isPending}
        onConfirm={handleConfirm}
        onClose={() => setShowConfirmModal(false)}
      />
    </PageWrapper>
  );
};
